import React, { useRef, useState } from 'react';
import {
  Pencil, Save, Check, AlertTriangle, Search, Filter, Download, Settings, Trash2, Home, Layers, Tag, FileText,
  List, Table, LayoutList, Link2, ChevronDown, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { previewStyle } from '../../../theme/themeRuntime';
import { checkContrast } from '../../../theme/themeGenerator';
import { inspectTokens } from './inspectTokens';
import logo from '../../../assets/logos/WM-Logo-CB-White-Header.png';
import useLogoMask from '../../../components/layout/useLogoMask';

// Sample pages, one per nav icon under Home. Home is only there to show the nav item's tokens.
export const PREVIEW_PAGES = [
  { id: 'grid', label: 'Grid', Icon: Table },
  { id: 'form', label: 'Form', Icon: FileText },
  { id: 'panels', label: 'Panels', Icon: Layers },
  { id: 'status', label: 'Status', Icon: Tag },
];

/**
 * One narrow, theme-scoped preview column body. The container carries the
 * theme's tokens as inline custom properties, so every real STUI class inside
 * it resolves against that theme and the app's active theme is untouched.
 * The layout mirrors the app shell (header, then a backdrop holding the sidebar
 * and the module panel) so each surface color covers about the share of the
 * swatch it covers on screen.
 *
 * The nav icons switch the module panel between sample pages, so each page stays short. Pass
 * page / onPage to share the page across several previews (compare); otherwise it keeps its own.
 *
 * With onInspect / onPick (the builder), hovering reports which tokens paint the item under
 * the pointer and clicking hands them over, so a color on screen leads to its token.
 */
const ThemePreview = ({ tokens, analyze = false, onInspect, onPick, page: pageProp, onPage }) => {
  const rootRef = useRef(null);
  const [ownPage, setOwnPage] = useState(PREVIEW_PAGES[0].id);
  const page = pageProp ?? ownPage;
  const current = PREVIEW_PAGES.find(p => p.id === page) || PREVIEW_PAGES[0];
  // A nav click browses instead of picking the nav item's tokens; hovering still names them.
  const go = (e, id) => { e.stopPropagation(); if (onPage) onPage(id); else setOwnPage(id); };
  const logoMask = useLogoMask(logo, 22);
  const contrast = analyze ? checkContrast(tokens) : [];
  const inspect = (e) => inspectTokens(e.target, rootRef.current, tokens);
  const handlers = onInspect || onPick ? {
    onMouseOver: onInspect ? e => onInspect(inspect(e)) : undefined,
    onMouseLeave: onInspect ? () => onInspect(null) : undefined,
    onClick: onPick ? e => onPick(inspect(e)) : undefined,
  } : {};
  return (
    // Hover and click only read what is under the pointer; nothing in the swatch is operable.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/mouse-events-have-key-events
    <div ref={rootRef} className="stui-theme-preview" style={previewStyle(tokens)} {...handlers}>
      <div className="stui-header">
        <span className="stui-header-left">
          <span role="img" aria-label="WM Logo" className="stui-header-logo" style={logoMask || undefined} />
          <span className="stui-header-title">DAWN</span>
        </span>
        <span className="stui-header-user-name">User Name</span>
      </div>

      <div className="stui-theme-pv-frame">
        <nav className="stui-nav-sidebar stui-collapsed">
          <div className="stui-nav-item">
            <span className="stui-nav-item-content"><Home size={14} className="stui-nav-item-icon" /></span>
          </div>
          {PREVIEW_PAGES.map(({ id, label, Icon }) => (
            <div
              key={id}
              role="button"
              tabIndex={0}
              title={label}
              aria-pressed={id === current.id}
              className={`stui-nav-item${id === current.id ? ' active' : ''}`}
              onClick={e => go(e, id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(e, id); } }}
            >
              <span className="stui-nav-item-content"><Icon size={14} className="stui-nav-item-icon" /></span>
            </div>
          ))}
        </nav>

        <div className="stui-module-container">
          <div className="stui-module-header">
            <div className="stui-module-header-left"><h1>{current.label}</h1></div>
            <div className="stui-module-header-actions">
              <button type="button" className="stui-crud-btn" tabIndex={-1}><Pencil size={14} /></button>
              <button type="button" className="stui-crud-btn" tabIndex={-1}><Save size={14} /></button>
            </div>
          </div>

          <div className="stui-theme-pv-body">
            {current.id === 'grid' && (
              <>
                <table className="stui-data-grid">
                  <thead><tr><th>Name</th><th>State</th></tr></thead>
                  <tbody>
                    <tr><td>Row one</td><td>Open</td></tr>
                    <tr className="stui-theme-pv-row-hover"><td>Row two</td><td>Hover</td></tr>
                    <tr>
                      <td className="editable-cell modified"><input tabIndex={-1} defaultValue="Row three" /></td>
                      <td className="editable-cell modified"><input tabIndex={-1} defaultValue="Edited" /></td>
                    </tr>
                  </tbody>
                </table>

                <div className="stui-theme-pv-cap">Delete mode, yes/no column</div>
                <table className="stui-data-grid delete-mode">
                  <thead><tr><th><input type="checkbox" className="stui-checkbox-input stui-checkbox-input-danger" checked readOnly tabIndex={-1} /></th><th>Name</th><th>Active</th></tr></thead>
                  <tbody>
                    <tr className="selected-for-delete">
                      <td><input type="checkbox" className="stui-checkbox-input stui-checkbox-input-danger" checked readOnly tabIndex={-1} /></td>
                      <td>Marked</td>
                      <td><div className="stui-boolean-display"><Check size={16} className="stui-boolean-check" /></div></td>
                    </tr>
                    <tr>
                      <td><input type="checkbox" className="stui-checkbox-input stui-checkbox-input-danger" checked={false} readOnly tabIndex={-1} /></td>
                      <td>Kept</td>
                      <td><div className="stui-boolean-display" /></td>
                    </tr>
                  </tbody>
                </table>

                <div className="stui-theme-pv-cap">Header controls</div>
                <div className="stui-theme-pv-row">
                  <div className="stui-module-header-tabs">
                    <button type="button" className="stui-tab-btn-icon active" tabIndex={-1}><List size={16} /></button>
                    <button type="button" className="stui-tab-btn-icon" tabIndex={-1}><Table size={16} /></button>
                  </div>
                  <button type="button" className="stui-crud-btn stui-delete-btn" tabIndex={-1}><Trash2 size={14} /></button>
                </div>
                <div className="stui-search-wrapper">
                  <Search size={16} className="stui-search-icon" />
                  <input className="stui-search-input" tabIndex={-1} placeholder="Search..." />
                </div>

                <div className="stui-theme-pv-cap">View tabs</div>
                <div className="stui-theme-pv-row">
                  <button type="button" className="stui-dataentry-tab active" tabIndex={-1}><Table size={14} /><span>Grid</span></button>
                  <button type="button" className="stui-dataentry-tab" tabIndex={-1}><LayoutList size={14} /><span>Form</span></button>
                </div>

                <div className="stui-theme-pv-cap">Paging</div>
                <div className="stui-pagination-nav">
                  <button type="button" className="stui-pagination-btn" disabled tabIndex={-1}><ChevronLeft size={14} /></button>
                  <span className="stui-pagination-page-info">1 of 4</span>
                  <button type="button" className="stui-pagination-btn" tabIndex={-1}><ChevronRight size={14} /></button>
                </div>
              </>
            )}

            {current.id === 'form' && (
              <>
                <div className="stui-theme-pv-row">
                  <button type="button" className="stui-btn stui-btn-primary" tabIndex={-1}>Primary</button>
                  <button type="button" className="stui-btn stui-btn-secondary" tabIndex={-1}>Secondary</button>
                </div>

                {/* Editable inputs: a readonly attribute would pull in the read-only fill */}
                <input className="stui-field-input" tabIndex={-1} defaultValue="Text input" />
                <input className="stui-field-input dirty" tabIndex={-1} defaultValue="Edited input" />
                <div className="stui-field-display">Display value</div>
                <div className="stui-field-display stui-field-readonly">Read-only value</div>
                <input className="stui-field-input derived" readOnly tabIndex={-1} value="Derived value" />
                <div className="stui-field-input fk">
                  <span className="stui-field-fk-value">Linked record</span>
                  <Link2 size={12} className="stui-field-fk-icon" />
                </div>
                <div className="stui-searchselect-trigger">
                  <span className="stui-searchselect-value">Lookup choice</span>
                  <ChevronDown size={14} className="stui-searchselect-chevron" />
                </div>
                <label className="stui-checkbox">
                  <input type="checkbox" className="stui-checkbox-input" checked readOnly tabIndex={-1} />
                  <span>Checked box</span>
                </label>

                <div className="stui-theme-pv-cap">Keyboard focus</div>
                <input className="stui-field-input stui-theme-pv-focus" tabIndex={-1} defaultValue="Focused input" />
              </>
            )}

            {current.id === 'panels' && (
              <>
                <div className="stui-detail-section-header"><span>Detail section</span></div>
                <button type="button" className="stui-list-item selected" tabIndex={-1}>
                  <span className="stui-list-item-primary">Selected item</span>
                  <span className="stui-list-item-secondary">List panel</span>
                </button>
                <button type="button" className="stui-list-item" tabIndex={-1}>
                  <span className="stui-list-item-primary">Another item</span>
                </button>

                <div className="stui-related-tabs">
                  <button type="button" className="stui-related-tab active" tabIndex={-1}>Items <span className="stui-related-tab-count">12</span></button>
                  <button type="button" className="stui-related-tab" tabIndex={-1}>Notes</button>
                </div>

                <div className="stui-theme-pv-cap">Panel: lines, corners, shadow</div>
                <div className="stui-theme-pv-card">
                  <span className="stui-theme-pv-card-title">Panel Details</span>
                  <span className="stui-sublabel">Muted helper text</span>
                </div>

                <div className="stui-modal stui-theme-pv-popup">
                  <div className="stui-modal-header"><h3>Popup</h3></div>
                  <div className="stui-modal-body">Popup body text</div>
                </div>
              </>
            )}

            {current.id === 'status' && (
              <>
                <div className="stui-theme-pv-cap">Icons</div>
                <div className="stui-theme-pv-icons">
                  <Search size={16} /><Filter size={16} /><Download size={16} /><Settings size={16} /><Trash2 size={16} />
                </div>

                <div className="stui-theme-pv-cap">Badges</div>
                <div className="stui-theme-pv-row">
                  <span className="stui-badge-primary">Primary</span>
                  <span className="stui-badge-green">PROD</span>
                  <span className="stui-badge-amber">Pending</span>
                  <span className="stui-badge-red">Failed</span>
                  <span className="stui-badge-blue">View</span>
                  <span className="stui-badge-purple">Query</span>
                  <span className="stui-badge-gray">Expired</span>
                </div>

                <div className="stui-theme-pv-cap">Selected text</div>
                <div><span className="stui-theme-pv-selected">Highlighted words</span> in a sentence.</div>

                <div className="stui-theme-pv-cap">Loading</div>
                <Loader2 size={16} className="stui-spinner" />
                {analyze && <div className="stui-theme-pv-cap">Contrast</div>}
                {analyze && (
                <div className="stui-theme-pv-contrast">
                  {contrast.map(c => (
                    <div key={c.label} className="stui-theme-pv-contrast-row" title={`${c.fg} on ${c.bg}`}>
                      <span>{c.label}</span>
                      <span className={c.pass ? 'stui-badge-green' : 'stui-badge-orange'}>
                        {c.pass ? <Check size={11} /> : <AlertTriangle size={11} />} {c.ratio.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
